<template>
  <ManualVerification
    v-if="
      individStore.verification.showManualVerification
    "
  />
  <DropdownMenu
    class="rounded-sm bg-white"
    :show-chevron="!isVerificationBlocked"
    :disabled="isVerificationBlocked"
  >
    <span
      :class="{
        'font-base': true,
        flex: true,
        'flex-row': true,
        'items-center': true,
        'gap-xs': true,
        'text-text-primary': !isVerificationBlocked,
        'text-text-disabled': isVerificationBlocked,
      }"
    >
      <img
        :class="[
          'h-md w-md',
          isVerificationBlocked ? 'grayscale opacity-60' : '',
        ]"
        src="@/assets/bankid-logo.png"
        alt="Bank-Id"
      />
      <span>Identifiering</span>
    </span>
    <template #dropdown>
      <MenuItem
        id="bank-id-identifiering"
        class="pb-none cursor-auto"
        :disabled="true"
      >
        <img class="h-md w-md" src="@/assets/bankid-logo.png" alt="Bank-Id" />
        <span>BankID identifiering</span>
      </MenuItem>
      <MenuItem
        id="inkommande-samtal"
        class="pl-lg p-2xs"
        :disabled="isVerificationBlocked"
        :tabindex="isVerificationBlocked ? -1 : 0"
        v-on:click="authenticatePerson(true)"
        v-on:keydown="(event: KeyboardEvent) => onMenuItemKeydown(event, () => authenticatePerson(true))"
      >
        - Inkommande samtal
      </MenuItem>
      <MenuItem
        id="utgaende-samtal"
        class="pl-lg p-2xs"
        :disabled="isVerificationBlocked"
        :tabindex="isVerificationBlocked ? -1 : 0"
        v-on:click="authenticatePerson(false)"
        v-on:keydown="(event: KeyboardEvent) => onMenuItemKeydown(event, () => authenticatePerson(false))"
      >
        - Utgående samtal
      </MenuItem>
      <MenuItem
        :disabled="isVerificationBlocked"
        :tabindex="isVerificationBlocked ? -1 : 0"
        v-on:click="manuallyAuthenticatePerson()"
        v-on:keydown="(event: KeyboardEvent) => onMenuItemKeydown(event, manuallyAuthenticatePerson)"
        ><FontAwesomeIcon
          id="manuell identifiering"
          class="text-text-brand"
          icon="fa-regular fa-user"
          size="lg"
        /><span>Manuell identifiering</span></MenuItem
      >
    </template>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { library } from "@fortawesome/fontawesome-svg-core";
import { faUser, faUserGroup } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { reactive } from "vue";

import DropdownMenu from "@/components/ui/DropdownMenu.vue";
import MenuItem from "@/components/ui/MenuItem.vue";
import ManualVerification from "@/components/verification/ManualVerification.vue";
import {
  SessionStatusType,
  SessionUser,
  ValidationProblemDetails,
  AuthenticateIndividRequest,
} from "@/composables/KundserviceAppApiClient";
import { useApi } from "@/composables/useApi";
import { useVerificationBlock } from "@/composables/useVerificationBlock";
import { useIndividStore } from "@/stores";

library.add(faUser, faUserGroup);

export type BankIDProps = {
  personnummer?: string;
};

const props = defineProps<BankIDProps>();
const individStore = useIndividStore();
const { isVerificationBlocked } = useVerificationBlock();
const state = reactive<{
  sessionId: string | null;
  sessionUser: SessionUser | null;
  error: ValidationProblemDetails | null;
}>({
  sessionId: null,
  sessionUser: null,
  error: null,
});
const apiClient = useApi();
const waitForPersonAuthenticationCheck = (millisecs: number) =>
  new Promise((r) => setTimeout(r, millisecs));

function manuallyAuthenticatePerson() {
  if (isVerificationBlocked.value) return;
  individStore.verification.showManualVerification = true;
}

function onMenuItemKeydown(event: KeyboardEvent, action: () => void) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
}

async function authenticatePerson(incoming: boolean) {
  if (isVerificationBlocked.value || !props.personnummer) return;

  individStore.onBeforeVerification(props.personnummer);
  state.error = null;

  try {
    const request =  new AuthenticateIndividRequest({
      personalIdentityNumber: props.personnummer,
      incoming,
    });

    const authenticateResponse = await apiClient.authenticateIndivid(request);
    state.sessionId = authenticateResponse.sessionId as string;
    let checkCount = 0;

    while (individStore.verification.isAuthenticating && checkCount < 13) {
      await waitForPersonAuthenticationCheck(3000);

      try {
        const sessionStatus = await apiClient.getSessionStatus(state.sessionId);
        if (
          sessionStatus.status === SessionStatusType.Authenticated &&
          sessionStatus.sessionUser != null
        ) {
          state.sessionUser = sessionStatus.sessionUser;
          individStore.onVerificationSuccess();
        }
      } catch (ex) {
        console.error("Checkstatus failure: " + ex);
        individStore.verification.isVerified = false;
      }
      checkCount++;
    }
    await apiClient.logoutSession(state.sessionId);
  } catch (e) {
    if (e instanceof ValidationProblemDetails) {
      state.error = e;
    }
    state.sessionId = null;
    individStore.verification.isVerified = false;
    individStore.verification.isAuthenticating = false;
  }

  individStore.verification.isAuthenticating = false;
}
</script>
